/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ReactExport from 'react-export-excel';
import moment from 'moment';
import useStore from '../../../../store';
import c from '../../../../constant';
import { toFormatCurrency } from '../../../../utils/format';
import { fileNameSummarizedReport } from '../../../../components/MarketDevelopment/Miscellaneous/export';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export default function ExcelSummarizedReportMonthlyComparison({
	tab,
	year,
	month,
	insuranceMarketItem = null,
	generalDataComparisonItem = null,
	lifeDataComparisonItem = null,
	...props
}) {
	const { t } = useTranslation();
	const [data, setData] = useState([]);
	const { currentLang: lang } = useStore((state) => state.currentLang);

	function formatCellValue(description, value) {
		const numericValue = Number(value) || 0;

		if (description === t('Number of Policies')) {
			return toFormatCurrency(numericValue, null, 0);
		}

		return toFormatCurrency(numericValue);
	}

	function makeRowInsuranceMarketData(description, insuranceType, values = []) {
		const data = Array.isArray(values) ? values : [];

		return {
			description,
			insuranceType,
			previousYear: formatCellValue(description, data?.[0]),
			currentYear: formatCellValue(description, data?.[1]),
			growth: data?.[2] ?? 0,
		};
	}

	function makeGeneralRowDataComparison(description, yearValue, values = []) {
		const data = Array.isArray(values) ? values : [];

		const row = {
			description,
			year: yearValue,
		};

		data?.forEach((value, index) => {
			row[`productName_${index}`] = formatCellValue(description, value);
		});

		return row;
	}

	function makeLifeRowDataComparison(description, yearValue, values = []) {
		const data = Array.isArray(values) ? values : [];

		const row = {
			description,
			year: yearValue,
		};

		data?.forEach((value, index) => {
			row[`productName_${index}`] = formatCellValue(description, value);
		});

		return row;
	}

	function buildInsuranceMarketDataSheet(items, sheetName) {
		if (!items) return null;

		const rows = [
			makeRowInsuranceMarketData(t('Gross Premium'), t('General Insurance'), items?.overall?.grossPremium?.GENERAL_INSURANCE),
			makeRowInsuranceMarketData(t('Gross Premium'), t('Life Insurance'), items?.overall?.grossPremium?.LIFE_INSURANCE),
			makeRowInsuranceMarketData(t('Gross Premium'), t('Total'), items?.overall?.grossPremium?.Total),

			makeRowInsuranceMarketData(t('Number of Policies'), t('General Insurance'), items?.overall?.numPolicies?.GENERAL_INSURANCE),
			makeRowInsuranceMarketData(t('Number of Policies'), t('Life Insurance'), items?.overall?.numPolicies?.LIFE_INSURANCE),
			makeRowInsuranceMarketData(t('Number of Policies'), t('Total'), items?.overall?.numPolicies?.Total),

			makeRowInsuranceMarketData(t('Sum Insured'), t('General Insurance'), items?.overall?.sumInsured?.GENERAL_INSURANCE),
			makeRowInsuranceMarketData(t('Sum Insured'), t('Life Insurance'), items?.overall?.sumInsured?.LIFE_INSURANCE),
			makeRowInsuranceMarketData(t('Sum Insured'), t('Total'), items?.overall?.sumInsured?.Total),

			makeRowInsuranceMarketData(t('Gross Claim Incurred'), t('General Insurance'), items?.overall?.claimIncurred?.GENERAL_INSURANCE),
			makeRowInsuranceMarketData(t('Gross Claim Incurred'), t('Life Insurance'), items?.overall?.claimIncurred?.LIFE_INSURANCE),
			makeRowInsuranceMarketData(t('Gross Claim Incurred'), t('Total'), items?.overall?.claimIncurred?.Total),

			makeRowInsuranceMarketData(t('Gross Claim Paid'), t('General Insurance'), items?.overall?.claimPaid?.GENERAL_INSURANCE),
			makeRowInsuranceMarketData(t('Gross Claim Paid'), t('Life Insurance'), items?.overall?.claimPaid?.LIFE_INSURANCE),
			makeRowInsuranceMarketData(t('Gross Claim Paid'), t('Total'), items?.overall?.claimPaid?.Total),
		];

		return {
			type: 'insuranceMarket',
			name: sheetName,
			data: rows,
		};
	}

	function buildGeneralDataComparisonSheet(items, sheetName) {
		if (!items) return null;

		const productTitles = Array.isArray(items?.general?.products) ? items?.general?.products : [];

		const rows = [
			makeGeneralRowDataComparison(t('Gross Premium'), `${year - 1}`, items?.general?.grosspremium?.[year - 1]),
			makeGeneralRowDataComparison(t('Gross Premium'), `${year}`, items?.general?.grosspremium?.[year]),

			makeGeneralRowDataComparison(t('Number of Policies'), `${year - 1}`, items?.general?.numpolicies?.[year - 1]),
			makeGeneralRowDataComparison(t('Number of Policies'), `${year}`, items?.general?.numpolicies?.[year]),

			makeGeneralRowDataComparison(t('Sum Insured'), `${year - 1}`, items?.general?.suminsured?.[year - 1]),
			makeGeneralRowDataComparison(t('Sum Insured'), `${year}`, items?.general?.suminsured?.[year]),

			makeGeneralRowDataComparison(t('Gross Claim Incurred'), `${year - 1}`, items?.general?.claimincurred?.[year - 1]),
			makeGeneralRowDataComparison(t('Gross Claim Incurred'), `${year}`, items?.general?.claimincurred?.[year]),

			makeGeneralRowDataComparison(t('Gross Claim Paid'), `${year - 1}`, items?.general?.claimpaid?.[year - 1]),
			makeGeneralRowDataComparison(t('Gross Claim Paid'), `${year}`, items?.general?.claimpaid?.[year]),
		];

		return {
			type: 'dataGenComparison',
			name: sheetName,
			data: rows,
			productTitles,
		};
	}

	function buildLifeDataComparisonSheet(items, sheetName) {
		if (!items) return null;

		const productTitles = Array.isArray(items?.life?.products) ? items?.life?.products : [];

		const rows = [
			makeLifeRowDataComparison(t('Gross Premium'), `${year - 1}`, items?.life?.grosspremium?.[year - 1]),
			makeLifeRowDataComparison(t('Gross Premium'), `${year}`, items?.life?.grosspremium?.[year]),

			makeLifeRowDataComparison(t('Number of Policies'), `${year - 1}`, items?.life?.numpolicies?.[year - 1]),
			makeLifeRowDataComparison(t('Number of Policies'), `${year}`, items?.life?.numpolicies?.[year]),

			makeLifeRowDataComparison(t('Sum Insured'), `${year - 1}`, items?.life?.suminsured?.[year - 1]),
			makeLifeRowDataComparison(t('Sum Insured'), `${year}`, items?.life?.suminsured?.[year]),

			makeLifeRowDataComparison(t('Gross Claim Incurred'), `${year - 1}`, items?.life?.claimincurred?.[year - 1]),
			makeLifeRowDataComparison(t('Gross Claim Incurred'), `${year}`, items?.life?.claimincurred?.[year]),

			makeLifeRowDataComparison(t('Gross Claim Paid'), `${year - 1}`, items?.life?.claimpaid?.[year - 1]),
			makeLifeRowDataComparison(t('Gross Claim Paid'), `${year}`, items?.life?.claimpaid?.[year]),
		];

		return {
			type: 'dataLifeComparison',
			name: sheetName,
			data: rows,
			productTitles,
		};
	}

	useEffect(() => {
		const sheets = [];

		if (insuranceMarketItem) {
			sheets.push(
				buildInsuranceMarketDataSheet(
					insuranceMarketItem,
					`${lang === 'en' ? `Insurance market data for ${moment(month + 1, 'M').format('MMMM')} ${year}` : `ទិន្នន័យក្នុងខែ ${t(moment(month + 1, 'M').format('MMMM'))} ${year}`}`,
				),
			);
		}

		if (generalDataComparisonItem) {
			sheets.push(
				buildGeneralDataComparisonSheet(
					generalDataComparisonItem,
					`${lang === 'en' ? `GI_Product for ${moment(month + 1, 'M').format('MMMM')} ${year}` : `ធានា.ទូទៅក្នុងខែ ${t(moment(month + 1, 'M').format('MMMM'))} ${year}`}`,
				),
			);
		}

		if (lifeDataComparisonItem) {
			sheets.push(
				buildLifeDataComparisonSheet(
					lifeDataComparisonItem,
					`${lang === 'en' ? `LI_Product for ${moment(month + 1, 'M').format('MMMM')} ${year}` : `ធានា.អាយុជីវិតក្នុងខែ ${t(moment(month + 1, 'M').format('MMMM'))} ${year}`}`,
				),
			);
		}

		setData(sheets.filter(Boolean));
	}, [tab, insuranceMarketItem, generalDataComparisonItem, lifeDataComparisonItem, lang, year, month]);

	return (
		<Box {...props}>
			<ExcelFile
				filename={fileNameSummarizedReport(month + 1, year)}
				element={
					<Button
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
					>
						{t('Export')}
					</Button>
				}
			>
				{data?.map((el, i) => {
					if (el?.type === 'insuranceMarket') {
						return (
							<ExcelSheet key={`sheet-insurance-${i}`} data={el?.data} name={t(el?.name)}>
								<ExcelColumn label={t('descriptionReport')} value="description" />
								<ExcelColumn label={t('Type of Insurance')} value="insuranceType" />
								<ExcelColumn label={`${t(moment(month + 1, 'M').format('MMMM'))}_${year - 1}`} value="previousYear" />
								<ExcelColumn label={`${t(moment(month + 1, 'M').format('MMMM'))}_${year}`} value="currentYear" />
								<ExcelColumn label={t('Growth')} value="growth" />
							</ExcelSheet>
						);
					}

					if (el?.type === 'dataGenComparison') {
						return (
							<ExcelSheet key={`sheet-comparison-${i}`} data={el?.data} name={t(el?.name)}>
								<ExcelColumn label={t('descriptionReport')} value="description" />
								<ExcelColumn label={t('Year')} value="year" />
								{el?.productTitles?.map((product, index) => (
									<ExcelColumn
										key={`product-col-${index}`}
										label={lang === 'kh' ? product?.productNameKh : product?.productNameEn}
										value={`productName_${index}`}
									/>
								))}
							</ExcelSheet>
						);
					}

					if (el?.type === 'dataLifeComparison') {
						return (
							<ExcelSheet key={`sheet-comparison-${i}`} data={el?.data} name={t(el?.name)}>
								<ExcelColumn label={t('descriptionReport')} value="description" />
								<ExcelColumn label={t('Year')} value="year" />
								{el?.productTitles?.map((product, index) => (
									<ExcelColumn
										key={`product-col-${index}`}
										label={lang === 'kh' ? product?.productNameKh : product?.productNameEn}
										value={`productName_${index}`}
									/>
								))}
							</ExcelSheet>
						);
					}

					return null;
				})}
			</ExcelFile>
		</Box>
	);
}
